import { CHAT_TYPES } from './action';

const initialState = {
  messages: [],
  loading: false,
  error: null,
  sending: false,
  sendError: null,
  deleting: false,
  deleteError: null,
  editing: false,
  editError: null,
  replyToMessage: null,
  originalMessages: {}, // Cache original messages keyed by their IDs

  // Per-tab message storage (fixes dual-store bug — single source of truth)
  tabMessages: { public: [], community: [], state: [] },
  tabLoading: { public: false, community: false, state: false },
  tabLoaded: { public: false, community: false, state: false },
  
  // Realtime socket state (Phase 11a)
  typingUsers: {},   // { 'public': { userId: true } }
  presence: {},      // { 'public': [burrowerId, ...] }
  
  // Conversation states
  conversations: [],
  conversationsLoading: false,
  conversationsError: null,
  conversationCreating: false,
  conversationCreationError: null,
  currentConversation: null,
  conversationUpdating: false,
  conversationUpdateError: null,
  activeConversation: null,
  conversationMessages: {}, // Messages for each conversation keyed by conversationId
  conversationMessagesLoading: false,
  conversationMessagesError: null,

  // Unread counts (watermark system)
  unreadCounts: {},       // { public: N, community: N, state_XX: N, convo_ID: N }
  unreadCountsLoading: false,

  // Queue system
  queue: {},              // Keyed by conversationId: { conversationId: [...requests] }
  queueLoading: false,

  // Reply-post system
  replyPosts: {},              // Map<messageId, { tallies, participantIds, replyCount, lastReplyAt }>
  userPostStates: {},          // Map<conversationId, { state, isMuted }> (REQ-06: keyed by conversationId)
  conversationMuteStates: {},  // Map<conversationId, boolean>
  messageReplies: {},          // Map<messageId, { replies: [], tallies: {}, totalCount: number }>

  // Admin challenges (REQ-18)
  adminChallenges: {},         // Map<conversationId, challengeObject>

  // Edit histories (REQ-25)
  editHistories: {},           // Map<messageId, [{originalText, editedAt}]>

  // Floating chat panel (desktop)
  chatPanelOpen: false,

  // Generic operation tracking for new action types
  operationLoading: false,
  operationError: null,

  // Queue knock state
  pendingKnock: null,
};

const chatReducer = (state = initialState, action) => {
  switch (action.type) {
    // Fetch chat messages
    case CHAT_TYPES.FETCH_CHAT_MESSAGES_REQUEST:
      return {
        ...state,
        loading: true,
        error: null
      };
    
    case CHAT_TYPES.FETCH_CHAT_MESSAGES_SUCCESS:
      return {
        ...state,
        messages: Array.isArray(action.payload) ? action.payload : [],
        loading: false,
        error: null
      };
    
    case CHAT_TYPES.FETCH_CHAT_MESSAGES_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload
      };
    
    // Send chat message
    case CHAT_TYPES.SEND_CHAT_MESSAGE_REQUEST:
      return {
        ...state,
        sending: true,
        sendError: null
      };
    
    case CHAT_TYPES.SEND_CHAT_MESSAGE_SUCCESS: {
      const msg = action.payload.message || action.payload;
      const tabKey = action.payload.tabKey || null;
      const newState = {
        ...state,
        messages: [...state.messages, msg],
        sending: false,
        sendError: null,
      };
      // Append to the correct tab's message list so the UI updates immediately
      if (tabKey && state.tabMessages[tabKey]) {
        newState.tabMessages = {
          ...state.tabMessages,
          [tabKey]: [...state.tabMessages[tabKey], msg]
        };
      }
      return newState;
    }
    
    case CHAT_TYPES.SEND_CHAT_MESSAGE_FAILURE:
      return {
        ...state,
        sending: false,
        sendError: action.payload
      };
    
    // Realtime socket message (Phase 11a) — append with dedupe by message id
    case CHAT_TYPES.RECEIVE_SOCKET_MESSAGE: {
      const msg = action.payload.message;
      const chatType = action.payload.chatType || msg?.chatType || 'global';
      const tabKey = action.payload.tabKey || (chatType === 'global' ? 'public' : chatType);
      if (!msg || !msg.id) return state;
      // Dedupe — skip if already present in the target tab
      const existing = state.tabMessages[tabKey] || [];
      if (existing.some(m => m.id === msg.id)) return state;
      const updatedTab = [...existing, msg];
      return {
        ...state,
        messages: state.messages.some(m => m.id === msg.id) ? state.messages : [...state.messages, msg],
        tabMessages: {
          ...state.tabMessages,
          [tabKey]: updatedTab
        }
      };
    }
    
    // Realtime typing indicator (Phase 11a)
    case CHAT_TYPES.RECEIVE_SOCKET_TYPING: {
      const { chatType, userId, isTyping } = action.payload;
      const tabKey = chatType === 'global' ? 'public' : chatType;
      const roomTyping = { ...(state.typingUsers[tabKey] || {}) };
      if (isTyping) roomTyping[userId] = true;
      else delete roomTyping[userId];
      return {
        ...state,
        typingUsers: { ...state.typingUsers, [tabKey]: roomTyping }
      };
    }
    
    // Realtime presence snapshot (Phase 11a)
    case CHAT_TYPES.RECEIVE_SOCKET_PRESENCE: {
      const { chatType, online } = action.payload;
      const tabKey = chatType === 'global' ? 'public' : chatType;
      return {
        ...state,
        presence: { ...state.presence, [tabKey]: Array.isArray(online) ? online : [] }
      };
    }
    
    // Delete chat message
    case CHAT_TYPES.DELETE_CHAT_MESSAGE_REQUEST:
      return {
        ...state,
        deleting: true,
        deleteError: null
      };
    
    case CHAT_TYPES.DELETE_CHAT_MESSAGE_SUCCESS: {
      const deletedId = action.payload;
      const updatedTabMessagesD = {};
      Object.keys(state.tabMessages).forEach(key => {
        updatedTabMessagesD[key] = state.tabMessages[key].filter(m => m.id !== deletedId);
      });
      return {
        ...state,
        messages: state.messages.filter(message => message.id !== deletedId),
        tabMessages: updatedTabMessagesD,
        deleting: false,
        deleteError: null
      };
    }
    
    case CHAT_TYPES.DELETE_CHAT_MESSAGE_FAILURE:
      return {
        ...state,
        deleting: false,
        deleteError: action.payload
      };
    
    // Edit chat message
    case CHAT_TYPES.EDIT_CHAT_MESSAGE_REQUEST:
      return {
        ...state,
        editing: true,
        editError: null
      };
    
    case CHAT_TYPES.EDIT_CHAT_MESSAGE_SUCCESS: {
      const editedMsg = action.payload;
      const updatedTabMessagesE = {};
      Object.keys(state.tabMessages).forEach(key => {
        updatedTabMessagesE[key] = state.tabMessages[key].map(m =>
          m.id === editedMsg.id ? editedMsg : m
        );
      });
      return {
        ...state,
        messages: state.messages.map(message => 
          message.id === editedMsg.id ? editedMsg : message
        ),
        tabMessages: updatedTabMessagesE,
        editing: false,
        editError: null
      };
    }
    
    case CHAT_TYPES.EDIT_CHAT_MESSAGE_FAILURE:
      return {
        ...state,
        editing: false,
        editError: action.payload
      };
    
    // Fetch single message (for reply context)
    case CHAT_TYPES.FETCH_CHAT_MESSAGE_REQUEST:
      return {
        ...state,
        // No need to change loading state for individual message fetch
      };
    
    case CHAT_TYPES.FETCH_CHAT_MESSAGE_SUCCESS:
      return {
        ...state,
        originalMessages: {
          ...state.originalMessages,
          [action.payload.id]: action.payload
        }
      };
    
    case CHAT_TYPES.FETCH_CHAT_MESSAGE_FAILURE:
      // Just log the error, no need to update state
      console.error('Failed to fetch original message:', action.payload);
      return state;
    
    // Reply functionality
    case CHAT_TYPES.SET_REPLY_TO_MESSAGE:
      return {
        ...state,
        replyToMessage: action.payload
      };
    
    case CHAT_TYPES.CLEAR_REPLY_TO_MESSAGE:
      return {
        ...state,
        replyToMessage: null
      };
    
    // Conversation creation
    case CHAT_TYPES.CREATE_CONVERSATION_REQUEST:
      return {
        ...state,
        conversationCreating: true,
        conversationCreationError: null
      };
    
    case CHAT_TYPES.CREATE_CONVERSATION_SUCCESS:
      return {
        ...state,
        conversations: [action.payload, ...state.conversations],
        conversationCreating: false,
        conversationCreationError: null,
        activeConversation: action.payload // Set as active when created
      };
    
    case CHAT_TYPES.CREATE_CONVERSATION_FAILURE:
      return {
        ...state,
        conversationCreating: false,
        conversationCreationError: action.payload
      };
    
    // Fetch conversations
    case CHAT_TYPES.FETCH_CONVERSATIONS_REQUEST:
      return {
        ...state,
        conversationsLoading: true,
        conversationsError: null
      };
    
    case CHAT_TYPES.FETCH_CONVERSATIONS_SUCCESS:
      return {
        ...state,
        conversations: Array.isArray(action.payload) ? action.payload : [],
        conversationsLoading: false,
        conversationsError: null
      };
    
    case CHAT_TYPES.FETCH_CONVERSATIONS_FAILURE:
      return {
        ...state,
        conversationsLoading: false,
        conversationsError: action.payload
      };
    
    // Fetch single conversation
    case CHAT_TYPES.FETCH_CONVERSATION_REQUEST:
      return {
        ...state,
        // No need to set loading for individual conversation
      };
    
    case CHAT_TYPES.FETCH_CONVERSATION_SUCCESS:
      return {
        ...state,
        currentConversation: action.payload,
        // Set as active conversation when fetched
        activeConversation: action.payload,
        // Also update the conversation in the list if it exists
        conversations: state.conversations.map(convo => 
          convo.id === action.payload.id ? action.payload : convo
        )
      };
    
    // Handle fetching conversation messages
    case CHAT_TYPES.FETCH_CONVERSATION_MESSAGES_REQUEST:
      return {
        ...state,
        conversationMessagesLoading: true,
        conversationMessagesError: null
      };
    
    case CHAT_TYPES.FETCH_CONVERSATION_MESSAGES_SUCCESS:
      return {
        ...state,
        conversationMessagesLoading: false,
        conversationMessagesError: null,
        conversationMessages: {
          ...state.conversationMessages,
          [action.payload.conversationId]: action.payload.messages
        }
      };
    
    case CHAT_TYPES.FETCH_CONVERSATION_MESSAGES_FAILURE:
      return {
        ...state,
        conversationMessagesLoading: false,
        conversationMessagesError: action.payload
      };
    
    case CHAT_TYPES.FETCH_CONVERSATION_FAILURE:
      console.error('Failed to fetch conversation:', action.payload);
      return state;
    
    // Update conversation status
    case CHAT_TYPES.UPDATE_CONVERSATION_STATUS_REQUEST:
      return {
        ...state,
        conversationUpdating: true,
        conversationUpdateError: null
      };
    
    case CHAT_TYPES.UPDATE_CONVERSATION_STATUS_SUCCESS:
      return {
        ...state,
        conversations: state.conversations.map(convo => 
          convo.id === action.payload.id 
            ? { ...convo, status: action.payload.status } 
            : convo
        ),
        currentConversation: state.currentConversation && state.currentConversation.id === action.payload.id
          ? { ...state.currentConversation, status: action.payload.status }
          : state.currentConversation,
        conversationUpdating: false,
        conversationUpdateError: null
      };
    
    case CHAT_TYPES.UPDATE_CONVERSATION_STATUS_FAILURE:
      return {
        ...state,
        conversationUpdating: false,
        conversationUpdateError: action.payload
      };
    
    // Set active conversation (UI state)
    case CHAT_TYPES.SET_ACTIVE_CONVERSATION:
      return {
        ...state,
        activeConversation: action.payload
      };
    
    case CHAT_TYPES.CLEAR_ACTIVE_CONVERSATION:
      return {
        ...state,
        activeConversation: null
      };

    // Unread counts (watermark system)
    case CHAT_TYPES.FETCH_UNREAD_COUNTS_REQUEST:
      return { ...state, unreadCountsLoading: true };

    case CHAT_TYPES.FETCH_UNREAD_COUNTS_SUCCESS:
      return { ...state, unreadCounts: action.payload, unreadCountsLoading: false };

    case CHAT_TYPES.FETCH_UNREAD_COUNTS_FAILURE:
      return { ...state, unreadCountsLoading: false };

    // Mark-as-read success: clear the count for the marked room/conversation
    case CHAT_TYPES.MARK_AS_READ_SUCCESS: {
      const updated = { ...state.unreadCounts };
      if (action.payload.roomId) {
        // Try to zero out the matching key
        Object.keys(updated).forEach(key => {
          if (key === action.payload.roomId) updated[key] = 0;
        });
      }
      if (action.payload.conversationId) {
        const convoKey = `convo_${action.payload.conversationId}`;
        updated[convoKey] = 0;
      }
      return { ...state, unreadCounts: updated };
    }

    // Queue system
    case CHAT_TYPES.FETCH_QUEUE_REQUEST:
      return { ...state, queueLoading: true };

    case CHAT_TYPES.FETCH_QUEUE_SUCCESS:
      return {
        ...state,
        queueLoading: false,
        queue: {
          ...state.queue,
          [action.payload.conversationId]: action.payload.queue
        }
      };

    case CHAT_TYPES.FETCH_QUEUE_FAILURE:
      return { ...state, queueLoading: false };

    case CHAT_TYPES.APPROVE_KNOCK_SUCCESS:
    case CHAT_TYPES.DENY_KNOCK_SUCCESS: {
      // Remove the processed request from the queue
      const newQueue = { ...state.queue };
      const convoId = action.payload.conversationId;
      if (newQueue[convoId]) {
        newQueue[convoId] = newQueue[convoId].filter(r => r.requestId !== action.payload.requestId);
      }
      return { ...state, queue: newQueue };
    }

    // Send conversation message — append to conversationMessages cache
    case CHAT_TYPES.SEND_CONVERSATION_MESSAGE_SUCCESS: {
      const cId = action.payload.conversationId;
      const existing = state.conversationMessages[cId] || [];
      return {
        ...state,
        conversationMessages: {
          ...state.conversationMessages,
          [cId]: [...existing, action.payload.message]
        }
      };
    }

    // ---- Tab-specific message fetching (per-tab storage) ----
    case CHAT_TYPES.FETCH_TAB_MESSAGES_REQUEST: {
      const reqTabKey = action.payload.tabKey;
      return {
        ...state,
        tabLoading: { ...state.tabLoading, [reqTabKey]: true }
      };
    }

    case CHAT_TYPES.FETCH_TAB_MESSAGES_SUCCESS: {
      const { tabKey: successTabKey, messages: tabMsgs } = action.payload;
      return {
        ...state,
        tabMessages: { ...state.tabMessages, [successTabKey]: Array.isArray(tabMsgs) ? tabMsgs : [] },
        tabLoading: { ...state.tabLoading, [successTabKey]: false },
        tabLoaded: { ...state.tabLoaded, [successTabKey]: true }
      };
    }

    case CHAT_TYPES.FETCH_TAB_MESSAGES_FAILURE: {
      const failTabKey = action.payload.tabKey;
      return {
        ...state,
        tabLoading: { ...state.tabLoading, [failTabKey]: false },
        tabLoaded: { ...state.tabLoaded, [failTabKey]: true }
      };
    }

    // ---- Reply-Post & Stance System ----

    case CHAT_TYPES.SET_REPLY_POST_DATA:
      return {
        ...state,
        replyPosts: {
          ...state.replyPosts,
          [action.payload.messageId]: {
            tallies: action.payload.tallies,
            participantIds: action.payload.participantIds,
            replyCount: action.payload.replyCount,
            lastReplyAt: action.payload.lastReplyAt,
            participantNames: action.payload.participantNames || {},
            participantAvatars: action.payload.participantAvatars || {}
          }
        }
      };

    case CHAT_TYPES.UPDATE_STANCE_TALLIES:
      return {
        ...state,
        replyPosts: {
          ...state.replyPosts,
          [action.payload.messageId]: {
            ...state.replyPosts[action.payload.messageId],
            tallies: action.payload.tallies
          }
        }
      };

    case CHAT_TYPES.BUMP_REPLY_POST: {
      // Reorder tabMessages to move reply-post to bottom (most recent activity)
      const bumpId = action.payload.messageId;
      const bumpTab = action.payload.tabKey;
      if (!bumpTab || !state.tabMessages[bumpTab]) return state;
      const tabMsgsCopy = [...state.tabMessages[bumpTab]];
      const bumpIdx = tabMsgsCopy.findIndex(m => m.userChatMessageId === bumpId);
      if (bumpIdx === -1) return state;
      const [bumped] = tabMsgsCopy.splice(bumpIdx, 1);
      tabMsgsCopy.push(bumped);
      return {
        ...state,
        tabMessages: { ...state.tabMessages, [bumpTab]: tabMsgsCopy }
      };
    }

    case CHAT_TYPES.FETCH_USER_POST_STATES_SUCCESS:
      return {
        ...state,
        userPostStates: action.payload || {}
      };

    case CHAT_TYPES.SET_USER_POST_STATE_SUCCESS:
      return {
        ...state,
        userPostStates: {
          ...state.userPostStates,
          [action.payload.conversationId]: { state: action.payload.state, isMuted: action.payload.isMuted }
        }
      };

    case CHAT_TYPES.LEAVE_CONVERSATION_SUCCESS: {
      // Update conversation participant count in the list
      // Fix: conversations are stored with .id (not ._id) — match both for safety
      const updatedConvos = state.conversations.map(c =>
        (c.id === action.payload.conversationId || c._id === action.payload.conversationId)
          ? { ...c, participantCount: action.payload.participantCount, isCurrentUserParticipant: false }
          : c
      );
      // Clear activeConversation if the user left the one they're viewing
      const clearActive = (state.activeConversation?.id === action.payload.conversationId ||
                           state.activeConversation?._id === action.payload.conversationId);
      return {
        ...state,
        conversations: updatedConvos,
        activeConversation: clearActive ? null : state.activeConversation
      };
    }

    // ---- Role Management (REQ-17) ----

    case CHAT_TYPES.PROMOTE_PARTICIPANT_SUCCESS:
      // Participant promoted — update conversation's participant list if cached
      return state;

    case CHAT_TYPES.TRANSFER_ADMIN_SUCCESS:
      // Admin transferred — UI should refetch conversation data
      return state;

    case CHAT_TYPES.KICK_PARTICIPANT_SUCCESS: {
      // Append system message to conversation messages if cached
      const kickConvoId = action.payload.conversationId;
      const kickMsgs = state.conversationMessages[kickConvoId];
      if (kickMsgs && action.payload.systemMessage) {
        return {
          ...state,
          conversationMessages: {
            ...state.conversationMessages,
            [kickConvoId]: [...kickMsgs, action.payload.systemMessage]
          }
        };
      }
      return state;
    }

    // ---- Admin Challenge System (REQ-18) ----

    case CHAT_TYPES.CREATE_CHALLENGE_SUCCESS:
      return {
        ...state,
        adminChallenges: {
          ...state.adminChallenges,
          [action.payload.conversationId]: action.payload.challenge
        }
      };

    case CHAT_TYPES.DISMISS_CHALLENGE_SUCCESS: {
      const dismissedChallenges = { ...state.adminChallenges };
      delete dismissedChallenges[action.payload.conversationId];
      return {
        ...state,
        adminChallenges: dismissedChallenges
      };
    }

    // ---- Privacy Management (REQ-22, REQ-23) ----

    case CHAT_TYPES.UPDATE_PRIVACY_SUCCESS: {
      const privConvo = action.payload.conversation;
      return {
        ...state,
        conversations: state.conversations.map(c =>
          c._id === action.payload.conversationId ? { ...c, ...privConvo } : c
        ),
        activeConversation: state.activeConversation?._id === action.payload.conversationId
          ? { ...state.activeConversation, ...privConvo }
          : state.activeConversation
      };
    }

    // ---- Edit History (REQ-25) ----

    case CHAT_TYPES.FETCH_EDIT_HISTORY_SUCCESS:
      return {
        ...state,
        editHistories: {
          ...state.editHistories,
          [action.payload.messageId]: action.payload.editHistory
        }
      };

    case CHAT_TYPES.TOGGLE_CONVERSATION_MUTE_SUCCESS:
      return {
        ...state,
        conversationMuteStates: {
          ...state.conversationMuteStates,
          [action.payload.conversationId]: action.payload.isMuted
        }
      };

    case CHAT_TYPES.FETCH_MESSAGE_REPLIES_SUCCESS:
      return {
        ...state,
        messageReplies: {
          ...state.messageReplies,
          [action.payload.messageId]: {
            replies: action.payload.replies,
            tallies: action.payload.tallies,
            totalCount: action.payload.totalCount
          }
        }
      };
    
    // ---- Queue Knock / Participant actions (REQ-21, REQ-20) ----

    case CHAT_TYPES.KNOCK_CONVERSATION_SUCCESS:
      // Store the knock request — UI can show "pending" state
      return {
        ...state,
        pendingKnock: action.payload, // the QueueRequest object
        operationLoading: false,
        operationError: null
      };

    case CHAT_TYPES.ADD_PARTICIPANT_SUCCESS: {
      // Optimistically increment participant count in conversation list
      const addConvoId = action.payload.conversationId;
      return {
        ...state,
        conversations: state.conversations.map(c =>
          (c._id === addConvoId || c.conversationId === addConvoId)
            ? { ...c, participantCount: (c.participantCount || 0) + 1 }
            : c
        ),
        operationLoading: false,
        operationError: null
      };
    }

    case CHAT_TYPES.CANCEL_KNOCK_SUCCESS:
      // Clear the pending knock
      return {
        ...state,
        pendingKnock: null,
        operationLoading: false,
        operationError: null
      };

    // ---- Generic REQUEST/FAILURE handlers for all new action types ----

    case CHAT_TYPES.KNOCK_CONVERSATION_REQUEST:
    case CHAT_TYPES.ADD_PARTICIPANT_REQUEST:
    case CHAT_TYPES.CANCEL_KNOCK_REQUEST:
    case CHAT_TYPES.PROMOTE_PARTICIPANT_REQUEST:
    case CHAT_TYPES.TRANSFER_ADMIN_REQUEST:
    case CHAT_TYPES.KICK_PARTICIPANT_REQUEST:
    case CHAT_TYPES.CREATE_CHALLENGE_REQUEST:
    case CHAT_TYPES.DISMISS_CHALLENGE_REQUEST:
    case CHAT_TYPES.UPDATE_PRIVACY_REQUEST:
    case CHAT_TYPES.FETCH_EDIT_HISTORY_REQUEST:
    case CHAT_TYPES.TOGGLE_CONVERSATION_MUTE_REQUEST:
    case CHAT_TYPES.FETCH_MESSAGE_REPLIES_REQUEST:
    case CHAT_TYPES.LEAVE_CONVERSATION_REQUEST:
    case CHAT_TYPES.SET_USER_POST_STATE_REQUEST:
    case CHAT_TYPES.FETCH_USER_POST_STATES_REQUEST:
      return { ...state, operationLoading: true, operationError: null };

    case CHAT_TYPES.KNOCK_CONVERSATION_FAILURE:
    case CHAT_TYPES.ADD_PARTICIPANT_FAILURE:
    case CHAT_TYPES.CANCEL_KNOCK_FAILURE:
    case CHAT_TYPES.PROMOTE_PARTICIPANT_FAILURE:
    case CHAT_TYPES.TRANSFER_ADMIN_FAILURE:
    case CHAT_TYPES.KICK_PARTICIPANT_FAILURE:
    case CHAT_TYPES.CREATE_CHALLENGE_FAILURE:
    case CHAT_TYPES.DISMISS_CHALLENGE_FAILURE:
    case CHAT_TYPES.UPDATE_PRIVACY_FAILURE:
    case CHAT_TYPES.FETCH_EDIT_HISTORY_FAILURE:
    case CHAT_TYPES.TOGGLE_CONVERSATION_MUTE_FAILURE:
    case CHAT_TYPES.FETCH_MESSAGE_REPLIES_FAILURE:
    case CHAT_TYPES.LEAVE_CONVERSATION_FAILURE:
    case CHAT_TYPES.SET_USER_POST_STATE_FAILURE:
    case CHAT_TYPES.FETCH_USER_POST_STATES_FAILURE:
      return { ...state, operationLoading: false, operationError: action.payload };

    // Floating chat panel (desktop)
    case CHAT_TYPES.OPEN_CHAT_PANEL:
      return { ...state, chatPanelOpen: true };
    case CHAT_TYPES.CLOSE_CHAT_PANEL:
      return { ...state, chatPanelOpen: false };
    case CHAT_TYPES.TOGGLE_CHAT_PANEL:
      return { ...state, chatPanelOpen: !state.chatPanelOpen };

    default:
      return state;
  }
};

export default chatReducer;
